import { 
  Users, 
  MapPin,
  DollarSign,
  Home
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

// Demographics Data
const ageDistributionData = [
  { ageGroup: "0-14", population: 237, percentage: 17.5 },
  { ageGroup: "15-24", population: 136, percentage: 10.0 },
  { ageGroup: "25-34", population: 133, percentage: 9.8 },
  { ageGroup: "35-49", population: 220, percentage: 16.2 },
  { ageGroup: "50-64", population: 328, percentage: 24.2 },
  { ageGroup: "65+", population: 301, percentage: 22.2 }
];

const populationByGender = [
  { name: "Female", value: 650, percentage: 48.0, color: "#8884d8" },
  { name: "Male", value: 705, percentage: 52.0, color: "#82ca9d" }
];

const educationData = [
  { level: "Bachelor Degree level and above", count: 60, percentage: 5.3 },
  { level: "Advanced Diploma and Diploma level", count: 72, percentage: 6.4 },
  { level: "Certificate level IV", count: 33, percentage: 2.9 },
  { level: "Certificate level III", count: 204, percentage: 18.1 },
  { level: "Year 12", count: 132, percentage: 11.7 },
  { level: "Year 11", count: 69, percentage: 6.1 },
  { level: "Year 10", count: 257, percentage: 22.8 },
  { level: "Year 9 or below", count: 149, percentage: 13.2 },
  { level: "Inadequately described", count: 12, percentage: 1.1 },
  { level: "No educational attainment", count: 3, percentage: 0.3 },
  { level: "Not stated", count: 126, percentage: 11.2 }
];

const occupationData = [
  { occupation: "Labourers", count: 106, percentage: 21.9 },
  { occupation: "Machinery Operators and Drivers", count: 75, percentage: 15.5 },
  { occupation: "Technicians and Trades Workers", count: 74, percentage: 15.3 },
  { occupation: "Sales Workers", count: 54, percentage: 11.2 },
  { occupation: "Community and Personal Service Workers", count: 47, percentage: 9.7 },
  { occupation: "Professionals", count: 36, percentage: 7.5 },
  { occupation: "Clerical and Administrative Workers", count: 36, percentage: 7.5 },
  { occupation: "Managers", count: 33, percentage: 6.8 }
];

const medianWeeklyIncome = [
  { category: "Personal", income: 514 },
  { category: "Family", income: 1293 },
  { category: "Household", income: 934 }
];

const labourForcePercentage = [
  { category: "In the labour force", count: 531, percentage: 47.2 },
  { category: "Not in the labour force", count: 492, percentage: 43.7 },
  { category: "Not stated", count: 103, percentage: 9.2 }
];

const familyCompositionData = [
  { type: "Couple family without children", households: 157, percentage: 43.4 },
  { type: "Couple family with children", households: 118, percentage: 32.6},
  { type: "One parent family", households: 74, percentage: 20.4 },
  { type: "Other family", households: 5, percentage: 1.4 }
];

const tenureTypeData = [
  { type: "Owned outright", households: 216, percentage: 37.2 },
  { type: "Owned with a mortgage", households: 187, percentage: 32.2 },
  { type: "Rented", households: 123, percentage: 21.2 },
  { type: "Other tenure type", households: 7, percentage: 1.2 },
  { type: "Tenure type not stated", households: 48, percentage: 8.3 }
];

const houseHoldComposition = [
  { type: "Family households", households: 357, percentage: 61.6 },
  { type: "Single (or lone) person households", households: 210, percentage: 36.2 },
  { type: "Group households", households: 13, percentage: 2.2 },
];

const healthData = [
  { indicator: "None of the selected conditions", percentage: 48.8 },
  { indicator: "One condition", percentage: 24.8 },
  { indicator: "Two or more conditions", percentage: 15.2 },
  { indicator: "Not stated", percentage: 10.9 }
];

const keyDemographics = [
  { label: "Total Population", value: "1,362", change: "ABS 2021 Census", icon: Users },
  { label: "Total Households", value: "647", change: "Average 2.2 persons", icon: Home },
  { label: "Median Age", value: "46 years", change: "Above national avg", icon: Users },
  { label: "Median Household Income", value: "$934/week", change: "", icon: DollarSign }
];

const chartColors = {
  color_1: "#FFBF00",
  color_2: "#13ad28ff", 
  color_3: "#1656ebff",
  color_4: "#ff005dff",
  color_5: "#B8860B",
  primary: "#FFBF00",
  secondary: "#FFD700",
};

export function VisualsTab() {
  return (
    <div className="section-container">
      <div className="section-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="section-title mb-2">Demographics</h2>
            <p className="section-text">Local demographic profile based on Australian Bureau of Statistics (ABS) data</p>
          </div>
          <div className="px-3 py-1 bg-black/40 text-secondary border border-white/10 rounded-lg text-sm flex items-center gap-2">
            <MapPin className="h-3 w-3" />
            ABS 2021 Census
          </div>
        </div>

        {/* Key Demographics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {keyDemographics.map((demo, index) => {
            const Icon = demo.icon;
            return (
              <div key={index} className="bg-black/40 border border-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 text-xs text-secondary mb-2">
                  <Icon className="h-4 w-4" />
                  {demo.label}
                </div>
                <div className="text-2xl font-bold text-white mb-1">{demo.value}</div>
                <p className="text-sm text-white/70">{demo.change}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* People and Population */}
      <div className="section-card">
        <h3 className="section-subtitle">People and Population</h3>
        <p className="section-text mb-6">Age distribution and population characteristics</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-3 text-white">Age Distribution</h4>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={ageDistributionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                <XAxis dataKey="ageGroup" stroke="#ffffff" />
                <YAxis stroke="#ffffff" />
                <Tooltip
                  formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Population']}
                  labelFormatter={(label) => `Age Group: ${label}`}
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #FFBF00', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFBF00' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Bar dataKey="population" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div>
            <h4 className="font-medium mb-3 text-white">Population by Gender</h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={populationByGender}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {populationByGender.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[`color_${index + 1}`]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #FFBF00', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFBF00' }}
                  itemStyle={{ color: '#ffffff' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Education */}
      <div className="section-card">
        <h3 className="section-subtitle">Education</h3>
        <p className="section-text mb-6">Level of highest educational attainment</p>
        
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={educationData.slice(0, 8)} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
            <XAxis type="number" stroke="#ffffff" />
            <YAxis dataKey="level" type="category" width={200} stroke="#ffffff" />
            <Tooltip
              formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Count']}
              labelFormatter={(label) => `Education: ${label}`}
              contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #FFBF00', borderRadius: '8px' }}
              labelStyle={{ color: '#FFBF00' }}
              itemStyle={{ color: '#ffffff' }}
            />
            <Bar dataKey="count" fill={chartColors.primary} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Income and Work */}
      <div className="section-card">
        <h3 className="section-subtitle">Income and Work</h3>
        <p className="section-text mb-6">Household income distribution and occupation categories</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-3 text-white">Median Weekly Incomes ($)</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={medianWeeklyIncome}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                <XAxis dataKey="category" stroke="#ffffff" />
                <YAxis stroke="#ffffff" />
                <Tooltip
                  formatter={(value, name) => [`$${value}`, 'Income']}
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #FFBF00', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFBF00' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Bar dataKey="income" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div>
            <h4 className="font-medium mb-3 text-white">Labour Force Participation</h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Tooltip
                  formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]}
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #FFBF00', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFBF00' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Pie
                  data={labourForcePercentage}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percentage }) => `${percentage}%`}
                  outerRadius={80}
                  dataKey="count"
                  nameKey="category"
                >
                  {labourForcePercentage.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[`color_${(index % 3) + 1}`]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3 space-y-1">
              {labourForcePercentage.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-white">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: chartColors[`color_${(index % 3) + 1}`] }}
                  ></div>
                  <span>{item.category}: {item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <div>
            <h4 className="font-medium mb-3 text-white">Top Occupations</h4>
            <div className="space-y-2">
              {occupationData.slice(0, 6).map((occ, index) => (
                <div key={index} className="flex justify-between items-center p-2 bg-black/40 border border-white/10 rounded">
                  <span className="text-sm text-white">{occ.occupation}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-secondary">{occ.count}</span>
                    <span className="text-xs text-white/70">({occ.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-3 text-white">Families & Housing</h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Tooltip
                  formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]}
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #FFBF00', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFBF00' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Pie
                  data={familyCompositionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percentage }) => `${percentage}%`}
                  outerRadius={80}
                  dataKey="households"
                  nameKey="type"
                >
                  {familyCompositionData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[`color_${(index % 4) + 1}`]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3 space-y-1">
              {familyCompositionData.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-white">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: chartColors[`color_${(index % 4) + 1}`] }}
                  ></div>
                  <span className="truncate">{item.type}: {item.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Housing Details */}
      <div className="section-card">
        <h3 className="section-subtitle">Housing & Health</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-3 text-white">Household Composition</h4>
            <div className="space-y-2">
              {houseHoldComposition.map((composition, index) => (
                <div key={index} className="flex justify-between items-center text-sm p-2 bg-black/40 border border-white/10 rounded">
                  <span className="text-white">{composition.type}</span>
                  <span className="font-medium text-secondary">{composition.percentage}%</span>
                </div>
              ))}
            </div>
            
            <h4 className="font-medium mb-3 mt-6 text-white">Tenure Type</h4>
            <div className="space-y-2">
              {tenureTypeData.slice(0, 4).map((tenure, index) => (
                <div key={index} className="flex justify-between items-center text-sm p-2 bg-black/40 border border-white/10 rounded">
                  <span className="text-white">{tenure.type}</span>
                  <span className="font-medium text-secondary">{tenure.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-3 text-white">Health Conditions</h4>
            <div className="grid grid-cols-2 gap-4">
              {healthData.map((health, index) => (
                <div key={index} className="p-4 border border-secondary/30 bg-black/40 rounded-lg text-center">
                  <div className="text-2xl font-medium mb-1 text-secondary">{health.percentage}%</div>
                  <div className="text-sm text-white/70">{health.indicator}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/50 italic mt-4">
              <strong>Note:</strong> Selected long-term health conditions include arthritis, asthma, cancer, 
              dementia, diabetes, heart disease, kidney disease, lung conditions, 
              mental health conditions and stroke.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
